import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Stack, Typography } from "@mui/material";
import { Button, Radio } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { useChurchStore } from "features/churches";
import {
  useBudgetYears,
  useCreateBudgetItem,
  useFinanceSettingsStore
} from "features/finances";
import { useTeams } from "features/teams";
import moment from "moment";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import * as yup from "yup";
import { ValidationError } from "../ValidationError";
import { BaseModal } from "./BaseModal";

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const Select = styled.select`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  font-family: inherit;
  flex: 1;
  background-color: transparent;
`;

const setBudgetItemSchema = yup.object({
  budget_year_id: yup.string().required("Budget year is required"),
  team_id: yup.string().required("Department is required"),
  item: yup.string().required("Item is required"),
  item_gross_amount: yup
    .number()
    .typeError("Gross amount must be a number")
    .transform(value => (isNaN(value) || value === "" ? 0 : value))
    .required("Gross amount is required"),
  is_split_by_quarters: yup.boolean().required("Split by quarters is required"),
  quarter_1_amount: yup
    .number()
    .typeError("Quarter 1 amount must be a number")
    .transform(value => (isNaN(value) || value === "" ? 0 : value))
    .notRequired(),
  quarter_2_amount: yup
    .number()
    .typeError("Quarter 2 amount must be a number")
    .transform(value => (isNaN(value) || value === "" ? 0 : value))
    .notRequired(),
  quarter_3_amount: yup
    .number()
    .typeError("Quarter 3 amount must be a number")
    .transform(value => (isNaN(value) || value === "" ? 0 : value))
    .notRequired(),
  quarter_4_amount: yup
    .number()
    .typeError("Quarter 4 amount must be a number")
    .transform(value => (isNaN(value) || value === "" ? 0 : value))
    .notRequired()
});

export function SetBudgetModal({ teamId, budgetYear }) {
  const setBudgetOpen = useFinanceSettingsStore(state => state.setBudgetOpen);
  const closeSetBudgetModal = useFinanceSettingsStore(
    state => state.closeSetBudgetModal
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const createBudgetItemMutation = useCreateBudgetItem();
  const teamsQuery = useTeams({
    churchId: selectedChurch?.id
  });
  const budgetYearsQuery = useBudgetYears({
    churchId: selectedChurch?.id
  });

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
    control
  } = useForm({
    defaultValues: {
      budget_year_id: budgetYear || "",
      team_id: teamId || "",
      item: "",
      item_gross_amount: 0,
      is_split_by_quarters: "true",
      quarter_1_amount: 0,
      quarter_2_amount: 0,
      quarter_3_amount: 0,
      quarter_4_amount: 0
    },
    resolver: yupResolver(setBudgetItemSchema)
  });

  useEffect(() => {
    if (
      teamId &&
      budgetYear &&
      teamsQuery.data?.result?.length > 0 &&
      budgetYearsQuery.data?.result?.length > 0
    ) {
      setValue("budget_year_id", budgetYear);
      setValue("team_id", teamId);
    }
  }, [teamId, budgetYear, teamsQuery.data, budgetYearsQuery.data]);

  // Add effect to set default budget year if none provided
  useEffect(() => {
    if (!budgetYear && budgetYearsQuery.data?.result) {
      const currentYear = moment().year();
      const currentBudgetYear = budgetYearsQuery.data.result.find(
        budgetYear => moment(budgetYear.start_date).year() === currentYear
      );
      if (currentBudgetYear) {
        setValue("budget_year_id", currentBudgetYear.id);
      }
    }
  }, [budgetYear, budgetYearsQuery.data, setValue]);

  const isSplit = watch("is_split_by_quarters") === "true";

  const onSubmit = values => {
    const budgetItemData = {
      budget_year_id: values.budget_year_id,
      team_id: values.team_id,
      item: values.item,
      item_gross_amount: values.item_gross_amount || 0,
      is_split_by_quarters: values.is_split_by_quarters
    };

    if (values.is_split_by_quarters) {
      budgetItemData["quarter_1_amount"] = values.quarter_1_amount || 0;
      budgetItemData["quarter_2_amount"] = values.quarter_2_amount || 0;
      budgetItemData["quarter_3_amount"] = values.quarter_3_amount || 0;
      budgetItemData["quarter_4_amount"] = values.quarter_4_amount || 0;
    } else {
      budgetItemData["quarter_1_amount"] =
        values.quarter === "quarter_1" ? values.item_gross_amount : 0;
      budgetItemData["quarter_2_amount"] =
        values.quarter === "quarter_2" ? values.item_gross_amount : 0;
      budgetItemData["quarter_3_amount"] =
        values.quarter === "quarter_3" ? values.item_gross_amount : 0;
      budgetItemData["quarter_4_amount"] =
        values.quarter === "quarter_4" ? values.item_gross_amount : 0;
    }

    createBudgetItemMutation.mutate(
      {
        churchId: selectedChurch?.id,
        budgetItemData
      },
      {
        onSuccess: () => {
          reset();
        }
      }
    );
  };

  const remainingBudget =
    watch("item_gross_amount") -
    watch("quarter_1_amount") -
    watch("quarter_2_amount") -
    watch("quarter_3_amount") -
    watch("quarter_4_amount");

  return (
    <BaseModal
      title="Add Budget Item"
      isOpen={setBudgetOpen}
      onClose={closeSetBudgetModal}
      footer={
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            flavor="white"
            size="small"
            onClick={closeSetBudgetModal}
            disabled={createBudgetItemMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={handleSubmit(onSubmit)}
            disabled={createBudgetItemMutation.isLoading}
          >
            Save
          </Button>
        </Stack>
      }
    >
      <Stack direction="row" spacing={2}>
        <Field>
          <Label htmlFor="department">Selected Department</Label>
          <Select id="department" {...register("team_id")}>
            <option value="" disabled>
              Select department
            </option>
            {teamsQuery.data?.result?.map(team => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </Select>
          <ErrorMessage
            errors={errors}
            name="team_id"
            render={({ message }) => <ValidationError message={message} />}
          />
        </Field>
        <Field>
          <Label htmlFor="budget_year_id">Budget Year</Label>
          <Select id="budget_year_id" {...register("budget_year_id")}>
            <option value="" disabled>
              Select budget year
            </option>
            {budgetYearsQuery.data?.result
              ?.filter(budgetYear => {
                const currentYear = moment().year();
                const budgetYearStart = moment(budgetYear.start_date).year();
                return budgetYearStart === currentYear;
              })
              .map(budgetYear => (
                <option key={budgetYear.id} value={budgetYear.id}>
                  {budgetYear.title}
                </option>
              ))}
          </Select>
          <ErrorMessage
            errors={errors}
            name="budget_year_id"
            render={({ message }) => <ValidationError message={message} />}
          />
        </Field>
      </Stack>
      <Field>
        <Label htmlFor="item">Item</Label>
        <Input {...register("item")} />
        <ErrorMessage
          errors={errors}
          name="item"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="item_gross_amount">Gross Amount For Item</Label>
        <Controller
          name="item_gross_amount"
          control={control}
          render={({ field }) => (
            <NumericFormat
              customInput={Input}
              id="item_gross_amount"
              thousandSeparator
              allowNegative={false}
              onValueChange={values => {
                field.onChange(values.floatValue);
              }}
              value={field.value}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name="item_gross_amount"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="is_split_by_quarters">
          Do you need to split the budget between Quarters
        </Label>
        <Radio
          label=""
          name="is_split_by_quarters"
          buttons={[
            {
              radioLabel: "Yes",
              value: true,
              id: "split_yes",
              labelStyle: {
                fontSize: "12px"
              }
            },
            {
              radioLabel: "No",
              value: false,
              id: "split_no",
              labelStyle: {
                fontSize: "12px"
              }
            }
          ]}
          row={true}
          register={register}
          error={false}
          helperText=""
        />
        <ErrorMessage
          errors={errors}
          name="is_split_by_quarters"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="quarter">Assign Budget Period</Label>
        <Typography
          variant="caption"
          sx={{
            color: "rgba(167, 167, 167, 1)"
          }}
        >
          You can assign the budget for each quarters based on gross amount.
        </Typography>
        {isSplit && (
          <>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "12px"
                }}
              >
                Quarter 1 (Jan, Feb, Mar)
              </Typography>
              <Controller
                name="quarter_1_amount"
                control={control}
                render={({ field }) => (
                  <NumericFormat
                    customInput={Input}
                    id="quarter_1_amount"
                    thousandSeparator
                    allowNegative={false}
                    onValueChange={values => {
                      field.onChange(values.floatValue);
                    }}
                    value={field.value}
                    style={{ flex: 1 }}
                  />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="quarter_1_amount"
                render={({ message }) => <ValidationError message={message} />}
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "12px"
                }}
              >
                Quarter 2 (Apr, May, Jun)
              </Typography>
              <Controller
                name="quarter_2_amount"
                control={control}
                render={({ field }) => (
                  <NumericFormat
                    customInput={Input}
                    id="quarter_2_amount"
                    thousandSeparator
                    allowNegative={false}
                    onValueChange={values => {
                      field.onChange(values.floatValue);
                    }}
                    value={field.value}
                    style={{ flex: 1 }}
                  />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="quarter_2_amount"
                render={({ message }) => <ValidationError message={message} />}
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "12px"
                }}
              >
                Quarter 3 (Jul, Aug, Sep)
              </Typography>
              <Controller
                name="quarter_3_amount"
                control={control}
                render={({ field }) => (
                  <NumericFormat
                    customInput={Input}
                    id="quarter_3_amount"
                    thousandSeparator
                    allowNegative={false}
                    onValueChange={values => {
                      field.onChange(values.floatValue);
                    }}
                    value={field.value}
                    style={{ flex: 1 }}
                  />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="quarter_3_amount"
                render={({ message }) => <ValidationError message={message} />}
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "12px"
                }}
              >
                Quarter 4 (Oct, Nov, Dec)
              </Typography>
              <Controller
                name="quarter_4_amount"
                control={control}
                render={({ field }) => (
                  <NumericFormat
                    customInput={Input}
                    id="quarter_4_amount"
                    thousandSeparator
                    allowNegative={false}
                    onValueChange={values => {
                      field.onChange(values.floatValue);
                    }}
                    value={field.value}
                    style={{ flex: 1 }}
                  />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="quarter_4_amount"
                render={({ message }) => <ValidationError message={message} />}
              />
            </Stack>
          </>
        )}

        {isSplit && (
          <Alert severity="info" sx={{ fontSize: "12px", mt: 2 }}>
            Remaining Budget: {Number(remainingBudget).toLocaleString()}
          </Alert>
        )}

        {!isSplit && (
          <Radio
            label=""
            name="quarter"
            buttons={[
              {
                radioLabel: "Quarter 1",
                value: "quarter_1",
                id: "quarter_1",
                labelStyle: {
                  fontSize: "12px"
                }
              },
              {
                radioLabel: "Quarter 2",
                value: "quarter_2",
                id: "quarter_2",
                labelStyle: {
                  fontSize: "12px"
                }
              },
              {
                radioLabel: "Quarter 3",
                value: "quarter_3",
                id: "quarter_3",
                labelStyle: {
                  fontSize: "12px"
                }
              },
              {
                radioLabel: "Quarter 4",
                value: "quarter_4",
                id: "quarter_4",
                labelStyle: {
                  fontSize: "12px"
                }
              }
            ]}
            row={false}
            register={register}
            error={false}
            helperText=""
          />
        )}
      </Field>

      <MutationError error={createBudgetItemMutation.error} sx={{ mt: 2 }} />
    </BaseModal>
  );
}
